/** Контент лендинга `/use-cases/conferences` (общая вёрстка в `UseCaseTimingPage`). */

import type { ConferencesOrganizerContent } from './conferencesOrganizerContent';
import { CONFERENCES_ORGANIZER_CONTENT } from './conferencesOrganizerContent';

/**
 * Hero-иллюстрация use-case (файл в `public/`).
 * Имя файла — латиница, ключевые слова для поиска по картинкам (Google, Яндекс).
 */
export const USE_CASE_TIMING_HERO_IMAGE = {
  src: '/online-timer-speaker-conference-hall.jpg',
  width: 1024,
  height: 571,
} as const;

export const useCaseProblemQuote = {
  text:
    'Сбитый тайминг — бич абсолютного большинства форумов, конференций и любых других событий',
  source: 'Ивентология',
} as const;

export const useCaseComparisonRows = [
  {
    label: 'Фокус',
    ordinary: 'Один отсчёт на одном устройстве',
    preztimer: 'Комната тайминга для события, спикера и организатора',
  },
  {
    label: 'Экран для спикера',
    ordinary: 'Нужно держать таймер рядом или показывать вручную',
    preztimer: 'Крупный браузерный экран с названием, прогрессом и зонами',
  },
  {
    label: 'Подсказки',
    ordinary: 'Жесты, записки или сообщения вне таймера',
    preztimer: 'Короткие сообщения прямо на экране таймера',
  },
  {
    label: 'Перерасход',
    ordinary: 'Не показывает, насколько блок вышел за регламент',
    preztimer: 'После нуля видно перерасход времени в формате +00:01',
  },
] as const;

export type UseCaseTimingHowStep = { title: string; text: string };

export type UseCaseTimingFaqItem = { question: string; answer: string };

export type UseCaseTimingHeroH1Accent = {
  lead: string;
  accent: string;
};

export type UseCaseTimingContent = {
  heroEyebrow: string;
  /** Визуальный H1: `lead` + акцент `accent`; полный текст — в `seo.h1` (`lead` + `accent`). */
  heroH1Accent?: UseCaseTimingHeroH1Accent;
  /** Alt для hero-изображения (уникальный под страницу; `src` — `USE_CASE_TIMING_HERO_IMAGE`). */
  heroImageAlt: string;
  problemTitle: string;
  problemLead: string;
  problemItems: readonly string[];
  solutionTitle: string;
  solutionLead: string;
  solutionItems: readonly string[];
  howSteps: readonly UseCaseTimingHowStep[];
  primaryBlock: {
    eyebrow: string;
    title: string;
    lead: string;
    bullets: readonly string[];
    tone: 'dark' | 'light';
  };
  crossLink: {
    eyebrow: string;
    title: string;
    lead: string;
    to: string;
    linkLabel: string;
  };
  faqSectionTitle: string;
  faqItems: readonly UseCaseTimingFaqItem[];
  /** Расширенный блок после «Три шага…» (только конференции, макет Stitch). */
  organizerSections?: ConferencesOrganizerContent;
};

export const CONFERENCES_TIMING_CONTENT: UseCaseTimingContent = {
  heroEyebrow: 'Конференции, секции и зал',
  heroH1Accent: { lead: 'Таймер для ', accent: 'конференций' },
  heroImageAlt:
    'Онлайн-таймер на экране ноутбука в конференц-зале: отсчёт времени, спикер на сцене и зрители в зале',
  problemTitle: 'Программа сдвигается, когда тайминг живёт отдельно от сцены',
  problemLead:
    'Несколько спикеров и передачи между слотами: если зал и бэкстейдж ориентируются на разные часы, перерасход одного блока тянет за собой весь день.',
  problemItems: [
    'Модератор передаёт сигнал жестами или в общем чате — спикер теряет темп и не видит остаток времени.',
    'Спикер узнаёт о перерасходе с задержкой, пока не вмешается команда.',
    'Организатор одновременно держит порядок выступлений, связь со сценой и ручной контроль времени.',
  ],
  solutionTitle: 'Контроль времени мероприятия в одном месте',
  solutionLead:
    'Панель управления и экран таймера для спикера синхронизированы: оператор ведёт очередь таймеров, спикер видит крупный отсчёт и короткие подсказки.',
  solutionItems: [
    'Одна комната на программу дня: доклады, панели, питчи и блок вопросов.',
    'Экран таймера для спикера и панель управления в одном сервисе.',
    'Синхронное время на всех подключённых устройствах.',
    'Сообщения на экране таймера: «завершайте», «Q&A» — без лишнего шума в зале.',
  ],
  howSteps: [
    {
      title: 'Создайте комнату',
      text: 'Добавьте таймеры под доклады, секции и переходы — порядок виден на панели.',
    },
    {
      title: 'Поделитесь ссылками',
      text: 'Спикерам и залу — экран таймера; команде — панель управления с любых устройств.',
    },
    {
      title: 'Ведите программу',
      text: 'Старт, пауза и переход к следующему таймеру вручную; подсказки уходят на экран спикера.',
    },
  ],
  primaryBlock: {
    eyebrow: 'Для конференций',
    title: 'Регламент для секций, докладов и модераторов',
    lead:
      'Модератор и оператор работают с одной панелью управления, спикер видит только экран таймера со временем и сообщениями.',
    bullets: [
      'команда в одной комнате с разных устройств;',
      'несколько таймеров удерживают расписание между секциями;',
      'цветовые зоны показывают, когда блок программы подходит к концу;',
      'сообщения предупреждают спикера, не прерывая выступление.',
    ],
    tone: 'dark',
  },
  crossLink: {
    eyebrow: 'Связанный сценарий',
    title: 'Короткие выступления, питчи и один экран',
    lead:
      'Один доклад или презентация без смены сетки слотов — отдельная страница с акцентом на регламент и быстрый старт.',
    to: '/use-cases/presentations',
    linkLabel: 'Таймер для презентаций и докладов',
  },
  organizerSections: CONFERENCES_ORGANIZER_CONTENT,
  faqSectionTitle: 'Частые вопросы о тайминге конференций',
  faqItems: [
    {
      question: 'Как вывести таймер для спикера на сцену?',
      answer:
        'Подключите экран и откройте ссылку на экран таймера в браузере. Переведите в полноэкранный режим и разместите там, где спикер смотрит: у трибуны, на полу сцены или в конце зала.',
    },
    {
      question: 'Как удержать спикера в регламенте?',
      answer:
        'Цветовые зоны показывают приближение к нулю. Отправьте короткое сообщение с панели управления — оно появится на экране таймера рядом с отсчётом.',
    },
    {
      question: 'Могут ли несколько человек вести тайминг?',
      answer:
        'Ссылку на панель управления можно открыть на нескольких устройствах: менеджер сцены и продюсер работают с одним состоянием комнаты. На лицензии Профи — приглашения в команду владельца.',
    },
    {
      question: 'Что будет при перезагрузке страницы?',
      answer:
        'Состояние комнаты хранится в облаке: таймеры, активный слот и сообщения восстанавливаются после обновления вкладки.',
    },
    {
      question: 'Можно ли вести несколько залов одновременно?',
      answer:
        'Да: отдельная комната на каждую сцену или трек. На лицензии Базовый — до трёх комнат на аккаунт, на Профи — без лимита.',
    },
    {
      question: 'Увидит ли спикер таймер на своём устройстве?',
      answer:
        'Да. Отправьте ссылку на экран таймера — спикер откроет её на телефоне, планшете или ноутбуке. Удобно для удалённых докладчиков по видеосвязи.',
    },
  ],
};
